{
  "$comment": "Single source of truth for map feature types. Read by web/src/featureTypes and (via `npm run gen`) by pocketbase/pb_hooks/lib/types.gen.js. Enum values are the Unity enum names (append-only there) and are only translated in the UI. Props with design:true are stored in designProps: never exported to Unity and not part of the sync hash. Prop kinds: text, textarea, int, number, bool, enum, enumMulti, color, featureRef, featureRefs, catalog, list (items = sub-props). Prop names are the Unity field names where one exists.",
  "version": 1,
  "statuses": ["idea", "proposed", "approved", "in_unity", "needs_update", "deprecated"],
  "geometryKinds": ["point", "circle", "rect", "polygon", "polyline", "none"],
  "catalogs": ["mobPrefabs", "npcPrefabs", "spawnPrefabs", "quests", "merchants", "trainers", "recipeTrainers", "factions", "harvestTypes", "lightingProfiles"],
  "types": {
    "zone": {
      "group": "areas",
      "keySegment": "",
      "geometries": ["polygon", "rect"],
      "icon": "zone",
      "color": "#3f9b4f",
      "showInGameDefault": true,
      "unity": { "component": "GeographicZone", "scene": "master", "idField": "zoneId", "idFromKey": "key" },
      "props": [
        { "name": "zoneId", "kind": "text", "unityId": true },
        { "name": "zoneType", "kind": "enum", "options": ["Village", "Forest", "Lake", "Meadows", "Coastal"], "default": "Forest" },
        { "name": "priority", "kind": "int", "default": 0 },
        { "name": "exitMargin", "kind": "number", "default": 0 },
        { "name": "unrevealedName", "kind": "text" },
        { "name": "bannerSubtitle", "kind": "text" },
        { "name": "bannerColor", "kind": "color" },
        { "name": "lightingProfile", "kind": "catalog", "catalog": "lightingProfiles" },
        { "name": "revealAfterQuest", "kind": "catalog", "catalog": "quests" },
        { "name": "linkedSpawnZone", "kind": "featureRef", "refTypes": ["mob_spawn_area"] },
        { "name": "levelMin", "kind": "int", "design": true },
        { "name": "levelMax", "kind": "int", "design": true },
        { "name": "mood", "kind": "textarea", "design": true }
      ]
    },
    "mob_spawn_area": {
      "group": "mobs",
      "keySegment": "spawn",
      "geometries": ["polygon", "rect"],
      "icon": "mob-spawn-area",
      "color": "#c0392b",
      "showInGameDefault": false,
      "unity": { "component": "MobSpawnZone", "scene": "master", "idField": null },
      "props": [
        { "name": "mode", "kind": "enum", "options": ["Random", "Defined"], "default": "Random" },
        {
          "name": "randomSpawns",
          "kind": "list",
          "items": [
            { "name": "mob", "kind": "catalog", "catalog": "mobPrefabs" },
            { "name": "weight", "kind": "number", "default": 1 },
            { "name": "maxCount", "kind": "int", "default": 3 },
            { "name": "respawnTime", "kind": "number", "default": 60 }
          ]
        },
        { "name": "cellSize", "kind": "number", "default": 50 },
        { "name": "cellSpawnChance", "kind": "number", "default": 0.3 },
        { "name": "maxPerCell", "kind": "int", "default": 2 },
        { "name": "activationRadius", "kind": "number", "default": 60 },
        { "name": "despawnRadius", "kind": "number", "default": 80 },
        { "name": "wanderRadius", "kind": "number", "default": 15 },
        { "name": "exclusionZones", "kind": "featureRefs", "refTypes": ["zone"] },
        { "name": "levelMin", "kind": "int", "design": true },
        { "name": "levelMax", "kind": "int", "design": true },
        { "name": "density", "kind": "enum", "options": ["sparse", "normal", "dense"], "design": true }
      ]
    },
    "mob_spawn_point": {
      "group": "mobs",
      "keySegment": "spawnpt",
      "geometries": ["point"],
      "icon": "mob-spawn-point",
      "color": "#e74c3c",
      "showInGameDefault": false,
      "parentTypes": ["mob_spawn_area"],
      "unity": { "component": "SubSpawnPoint", "scene": "master", "idField": null },
      "props": [
        { "name": "mob", "kind": "catalog", "catalog": "mobPrefabs" },
        { "name": "respawnTime", "kind": "number", "default": 60 },
        { "name": "movement", "kind": "enum", "options": ["Wander", "Waypoints"], "default": "Wander" },
        { "name": "patrolPath", "kind": "featureRef", "refTypes": ["path"] }
      ]
    },
    "npc": {
      "group": "characters",
      "keySegment": "npc",
      "geometries": ["point"],
      "icon": "npc",
      "color": "#f1c40f",
      "showInGameDefault": false,
      "unity": { "component": "NPC", "scene": "master", "idField": null },
      "props": [
        { "name": "actorId", "kind": "text" },
        { "name": "title", "kind": "text" },
        { "name": "prefab", "kind": "catalog", "catalog": "npcPrefabs" },
        { "name": "roles", "kind": "enumMulti", "options": ["QuestGiver", "Merchant", "Trainer", "RecipeTrainer", "Dialogue", "SequenceActor"] },
        { "name": "merchantId", "kind": "catalog", "catalog": "merchants" },
        { "name": "trainerId", "kind": "catalog", "catalog": "trainers" },
        { "name": "recipeTrainerId", "kind": "catalog", "catalog": "recipeTrainers" },
        { "name": "factionId", "kind": "catalog", "catalog": "factions" },
        { "name": "patrolArea", "kind": "featureRef", "refTypes": ["patrol_area"] },
        { "name": "patrolRadius", "kind": "number" },
        { "name": "personality", "kind": "textarea", "design": true }
      ]
    },
    "poi": {
      "group": "places",
      "keySegment": "poi",
      "geometries": ["point"],
      "icon": "poi",
      "color": "#8e44ad",
      "showInGameDefault": true,
      "unity": { "component": "MapPoi", "scene": "master", "idField": "poiId", "idFromKey": "key" },
      "props": [
        { "name": "poiId", "kind": "text", "unityId": true },
        { "name": "poiType", "kind": "enum", "options": ["Landmark", "Merchant", "SpawnPoint", "Home", "QuestGiver", "Dungeon"], "default": "Landmark" },
        { "name": "markerColor", "kind": "color" },
        { "name": "showLabel", "kind": "bool", "default": true },
        { "name": "icon", "kind": "text" }
      ]
    },
    "quest": {
      "group": "quests",
      "keySegment": "quest",
      "geometries": ["none", "point"],
      "icon": "quest",
      "color": "#e67e22",
      "showInGameDefault": false,
      "unity": { "component": "QuestData", "scene": "asset", "idField": "questID", "idFromKey": "slug" },
      "props": [
        { "name": "questID", "kind": "text", "unityId": true },
        { "name": "category", "kind": "text" },
        { "name": "requiredLevel", "kind": "int" },
        { "name": "giverNpc", "kind": "featureRef", "refTypes": ["npc"] },
        { "name": "turnInNpc", "kind": "featureRef", "refTypes": ["npc"] },
        { "name": "mapZone", "kind": "featureRef", "refTypes": ["zone"] },
        { "name": "mapPoi", "kind": "featureRef", "refTypes": ["poi"] },
        { "name": "repeatable", "kind": "bool", "default": false },
        { "name": "prerequisites", "kind": "featureRefs", "refTypes": ["quest"] },
        { "name": "rewardsIdea", "kind": "textarea", "design": true }
      ]
    },
    "quest_step": {
      "group": "quests",
      "keySegment": "step",
      "geometries": ["none", "point", "circle", "polygon"],
      "icon": "quest-step",
      "color": "#f39c12",
      "showInGameDefault": false,
      "parentTypes": ["quest"],
      "unity": { "component": "QuestObjectiveData", "scene": "asset", "idField": null },
      "props": [
        { "name": "stepIndex", "kind": "int", "default": 1 },
        { "name": "objectiveType", "kind": "enum", "options": ["Kill", "Collect", "UseItem", "Produce", "TalkToNPC", "Escort", "GoToLocation", "CustomEvent", "Harvest"], "default": "GoToLocation" },
        { "name": "target", "kind": "text" },
        { "name": "requiredCount", "kind": "int", "default": 1 },
        { "name": "optional", "kind": "bool", "default": false },
        { "name": "location", "kind": "featureRef", "refTypes": ["quest_location", "zone"] }
      ]
    },
    "quest_location": {
      "group": "quests",
      "keySegment": "loc",
      "geometries": ["rect", "circle"],
      "icon": "quest-location",
      "color": "#d35400",
      "showInGameDefault": false,
      "unity": { "component": "QuestLocationTrigger", "scene": "master", "idField": "locationTag", "idFromKey": "prefix_slug" },
      "props": [
        { "name": "locationTag", "kind": "text", "unityId": true },
        { "name": "destroyOnEnter", "kind": "bool", "default": false },
        { "name": "height", "kind": "number", "default": 10 }
      ]
    },
    "resource_node": {
      "group": "objects",
      "keySegment": "res",
      "geometries": ["point", "circle", "polygon"],
      "icon": "resource",
      "color": "#27ae60",
      "showInGameDefault": false,
      "unity": { "component": "ChunkSpawnPoint", "scene": "master", "idField": "spawnTargetId" },
      "props": [
        { "name": "spawnTargetId", "kind": "text", "unityId": true },
        { "name": "spawnPrefab", "kind": "catalog", "catalog": "spawnPrefabs" },
        { "name": "harvestTypeId", "kind": "catalog", "catalog": "harvestTypes" },
        { "name": "note", "kind": "text" },
        { "name": "count", "kind": "int", "design": true }
      ]
    },
    "altar": {
      "group": "objects",
      "keySegment": "altar",
      "geometries": ["point"],
      "icon": "altar",
      "color": "#9b59b6",
      "showInGameDefault": false,
      "unity": { "component": "ChunkSpawnPoint", "scene": "master", "idField": "spawnTargetId" },
      "props": [
        { "name": "spawnTargetId", "kind": "text", "unityId": true },
        { "name": "spawnPrefab", "kind": "catalog", "catalog": "spawnPrefabs" },
        { "name": "note", "kind": "text" }
      ]
    },
    "loot_container": {
      "group": "objects",
      "keySegment": "chest",
      "geometries": ["point"],
      "icon": "chest",
      "color": "#b7950b",
      "showInGameDefault": false,
      "unity": { "component": "ChunkSpawnPoint", "scene": "master", "idField": "spawnTargetId" },
      "props": [
        { "name": "spawnTargetId", "kind": "text", "unityId": true },
        { "name": "spawnPrefab", "kind": "catalog", "catalog": "spawnPrefabs" },
        { "name": "note", "kind": "text" },
        { "name": "lootIdea", "kind": "textarea", "design": true }
      ]
    },
    "interactable": {
      "group": "objects",
      "keySegment": "obj",
      "geometries": ["point"],
      "icon": "interactable",
      "color": "#16a085",
      "showInGameDefault": false,
      "unity": { "component": "ChunkSpawnPoint", "scene": "master", "idField": "spawnTargetId" },
      "props": [
        { "name": "spawnTargetId", "kind": "text", "unityId": true },
        { "name": "spawnPrefab", "kind": "catalog", "catalog": "spawnPrefabs" },
        { "name": "note", "kind": "text" }
      ]
    },
    "respawn_point": {
      "group": "places",
      "keySegment": "respawn",
      "geometries": ["point"],
      "icon": "respawn",
      "color": "#2980b9",
      "showInGameDefault": false,
      "unity": { "component": "RespawnPoint", "scene": "persistent", "idField": null },
      "props": []
    },
    "player_start": {
      "group": "places",
      "keySegment": "start",
      "geometries": ["point"],
      "icon": "player-start",
      "color": "#1abc9c",
      "showInGameDefault": false,
      "unity": { "component": "NetworkStartPosition", "scene": "persistent", "idField": null },
      "props": []
    },
    "patrol_area": {
      "group": "characters",
      "keySegment": "patrol",
      "geometries": ["polygon", "rect"],
      "icon": "patrol",
      "color": "#7f8c8d",
      "showInGameDefault": false,
      "unity": { "component": "PatrolZone", "scene": "master", "idField": null },
      "props": []
    },
    "path": {
      "group": "annotations",
      "keySegment": "path",
      "geometries": ["polyline"],
      "icon": "path",
      "color": "#a0522d",
      "showInGameDefault": false,
      "unity": { "component": "Waypoints", "scene": "master", "idField": null },
      "props": [
        { "name": "purpose", "kind": "enum", "options": ["road", "trail", "river", "patrol", "quest_route"], "default": "trail" },
        { "name": "width", "kind": "number", "design": true }
      ]
    },
    "note": {
      "group": "annotations",
      "keySegment": "note",
      "geometries": ["point", "circle", "rect", "polygon", "polyline"],
      "icon": "note",
      "color": "#ecf0f1",
      "showInGameDefault": false,
      "unity": null,
      "props": []
    }
  },
  "groups": ["areas", "places", "characters", "mobs", "quests", "objects", "annotations"],
  "presets": [
    { "id": "village", "type": "zone", "geometry": "polygon", "props": { "zoneType": "Village", "priority": 5 } },
    { "id": "forest", "type": "zone", "geometry": "polygon", "props": { "zoneType": "Forest" } },
    { "id": "meadows", "type": "zone", "geometry": "polygon", "props": { "zoneType": "Meadows" } },
    { "id": "lake", "type": "zone", "geometry": "polygon", "props": { "zoneType": "Lake" } },
    { "id": "coastal", "type": "zone", "geometry": "polygon", "props": { "zoneType": "Coastal" } },
    { "id": "landmark", "type": "poi", "geometry": "point", "props": { "poiType": "Landmark" } },
    { "id": "dungeon", "type": "poi", "geometry": "point", "props": { "poiType": "Dungeon" } },
    { "id": "npc", "type": "npc", "geometry": "point", "props": {} },
    { "id": "mob_area", "type": "mob_spawn_area", "geometry": "polygon", "props": { "mode": "Random" } },
    { "id": "mob_point", "type": "mob_spawn_point", "geometry": "point", "props": {} },
    { "id": "quest", "type": "quest", "geometry": "point", "props": {} },
    { "id": "quest_step", "type": "quest_step", "geometry": "point", "props": {} },
    { "id": "quest_area", "type": "quest_location", "geometry": "circle", "props": {} },
    { "id": "herb_patch", "type": "resource_node", "geometry": "circle", "props": {} },
    { "id": "resource", "type": "resource_node", "geometry": "point", "props": {} },
    { "id": "altar", "type": "altar", "geometry": "point", "props": {} },
    { "id": "chest", "type": "loot_container", "geometry": "point", "props": {} },
    { "id": "interactable", "type": "interactable", "geometry": "point", "props": {} },
    { "id": "respawn", "type": "respawn_point", "geometry": "point", "props": {} },
    { "id": "player_start", "type": "player_start", "geometry": "point", "props": {} },
    { "id": "patrol_area", "type": "patrol_area", "geometry": "polygon", "props": {} },
    { "id": "road", "type": "path", "geometry": "polyline", "props": { "purpose": "road" } },
    { "id": "river", "type": "path", "geometry": "polyline", "props": { "purpose": "river" } },
    { "id": "note", "type": "note", "geometry": "point", "props": {} },
    { "id": "note_area", "type": "note", "geometry": "polygon", "props": {} }
  ]
}
